import React from "react";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import "./RowConfiguration.scss";

/**
 * RowConfiguration Component
 * Properties panel for configuring row container settings
 * @param {Object} element - The row container element
 * @param {Function} onChange - Callback for property changes
 * @author Sarthak Arora
 */
const RowConfiguration = ({ element, onChange }) => {
  const { properties } = element;
  const { columnCount = 2, rowCount = 1, gap = 8, label = "Grid Layout" } = properties;

  // Column count options (1-4)
  const columnOptions = [
    { text: "1", value: 1 },
    { text: "2", value: 2 },
    { text: "3", value: 3 },
    { text: "4", value: 4 },
    { text: "5", value: 5 },
    { text: "6", value: 6 },
  ];

  // Row count options (1-10)
  const rowOptions = Array.from({ length: 10 }, (_, i) => ({
    text: `${i + 1} ${i === 0 ? "" : ""}`,
    value: i + 1,
  }));

  // Handle column count change
  const handleColumnCountChange = (event) => {
    const newColumnCount = parseInt(event.target.value);

    // Validate the column count
    if (isNaN(newColumnCount) || newColumnCount < 1 || newColumnCount > 4) {
      console.warn("Invalid column count:", newColumnCount);
      return;
    }

    const equalWidth = 100 / newColumnCount;

    // Create new column widths array
    const newColumnWidths = Array(newColumnCount).fill(equalWidth);

    onChange(element.id, {
      ...properties,
      columnCount: newColumnCount,
      columnWidths: newColumnWidths,
    });
  };

  // Handle row count change
  const handleRowCountChange = (event) => {
    const newRowCount = parseInt(event.target.value);

    // Validate the row count
    if (isNaN(newRowCount) || newRowCount < 1 || newRowCount > 10) {
      console.warn("Invalid row count:", newRowCount);
      return;
    }

    onChange(element.id, {
      ...properties,
      rowCount: newRowCount,
    });
  };

  // Handle gap change
  const handleGapChange = (event) => {
    const newGap = event.value !== undefined ? event.value : event.target.value;
    onChange(element.id, {
      ...properties,
      gap: newGap,
    });
  };

  // Handle label change
  const handleLabelChange = (event) => {
    onChange(element.id, {
      ...properties,
      label: event.target.value,
    });
  };

  return (
    <div className='row-configuration'>
      <div className='properties-section'>
        <h4 className='section-title border-bottom'>Grid Layout Settings</h4>

        {/* Column Count */}
        <div className='row'>
          <div className='form-group col-md-6'>
            <label className='form-label'>Columns</label>
            <select className='form-control' value={columnCount} onChange={handleColumnCountChange}>
              {columnOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
            <small className='form-text text-muted'>1-4 columns</small>
          </div>

          {/* Row Count */}
          <div className='form-group col-md-6'>
            <label className='form-label'>Rows</label>
            <select className='form-control' value={rowCount} onChange={handleRowCountChange}>
              {rowOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
            <small className='form-text text-muted'>1-10 rows</small>
          </div>

          <div className='col-md-12'>
            <h4 className='section-title border-bottom'>Spacing</h4>
          </div>

          {/* Column Gap */}
          <div className='form-group col-md-6'>
            <label className='form-label'>Column</label>
            <NumericTextBox value={gap} onChange={handleGapChange} min={0} max={50} step={4} format='n0' className='w-100' />
            <small className='form-text text-muted'>0-50px</small>
          </div>
          <div className='form-group col-md-6'>
            <label className='form-label'>Rows</label>
            <NumericTextBox disabled value={8} min={0} max={50} step={4} format='n0' className='w-100' />
            <small className='form-text text-muted'>0-50px</small>
          </div>
        </div>

        {/* Usage Instructions */}
        {/* <div className='usage-instructions mt-4'>
          <h5>How to use:</h5>
          <ul>
            <li>Drag components from the library into any grid cell</li>
            <li>Click on elements within cells to select and edit them</li>
            <li>Click on the grid border to select the entire grid layout</li>
            <li>Adjust row count, column count, and spacing using the controls above</li>
            <li>Use "Add Row" button to add more rows to existing grids</li>
          </ul>
        </div> */}
      </div>
    </div>
  );
};

export default RowConfiguration;
